// src/hr/pages/CalendarPage.js
import React, { useEffect, useState, useMemo } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  collection, query, where, onSnapshot, addDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaCalendarAlt, FaTasks, FaGlobe, FaStar } from "react-icons/fa";

const locales = { "en-US": require("date-fns/locale/en-US") };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const CalendarPage = () => {
  const { user } = useEmployeeAuth();
  const [holidays, setHolidays] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState({
    company: true,
    public: true,
    tasks: true
  });

  // Load Company + Public Holidays
  useEffect(() => {
    const q = query(collection(db, "holidays"));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setHolidays(data);
    });
    return () => unsub();
  }, []);

  // Load Tasks (with dueDate)
  useEffect(() => {
    const q = query(collection(db, "tasks"), where("dueDate", "!=", ""));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTasks(data);
    });
    return () => unsub();
  }, []);

  // Public Holidays (India 2025)
  const publicHolidays = [
    { title: "Republic Day", date: "2025-01-26", type: "public" },
    { title: "Independence Day", date: "2025-08-15", type: "public" },
    { title: "Gandhi Jayanti", date: "2025-10-02", type: "public" },
    { title: "Diwali", date: "2025-10-20", type: "public" },
    { title: "Christmas", date: "2025-12-25", type: "public" },
  ].map((h, i) => ({ ...h, id: `pub-${i}` }));

  // Build Events
  const events = useMemo(() => {
    const ev = [];

    // Company Holidays
    if (filters.company) {
      holidays.filter(h => h.type === "company").forEach(h => {
        ev.push({
          id: h.id,
          title: h.title,
          start: new Date(h.date),
          end: new Date(h.date),
          allDay: true,
          type: "company",
          desc: h.description
        });
      });
    }

    // Public Holidays
    if (filters.public) {
      publicHolidays.forEach(h => {
        ev.push({
          ...h,
          start: new Date(h.date),
          end: new Date(h.date),
          allDay: true
        });
      });
    }

    // Tasks
    if (filters.tasks) {
      tasks.forEach(t => {
        if (user.role === "hr" || t.employeeEmails?.includes(user.email)) {
          ev.push({
            id: t.id,
            title: `${t.taskTitle}`,
            start: new Date(t.dueDate),
            end: new Date(t.dueDate),
            allDay: false,
            type: "task",
            priority: t.priority
          });
        }
      });
    }

    return ev;
  }, [holidays, tasks, filters, user]);

  const eventStyle = (event) => {
    const colors = {
      company: "from-emerald-500 to-teal-500",
      public: "from-purple-500 to-pink-500",
      task: event.priority === "high" ? "from-red-500 to-rose-500" :
             event.priority === "low" ? "from-cyan-500 to-blue-500" :
             "from-yellow-400 to-orange-400"
    };
    return {
      style: {
        background: `linear-gradient(135deg, ${event.type === 'task' ? '#06b6d4' : '#8b5cf6'})`,
        borderRadius: "12px",
        color: "white",
        border: "none",
        fontWeight: "bold",
        padding: "4px 8px",
        boxShadow: "0 4px 15px rgba(0,0,0,0.3)"
      }
    };
  };

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-br from-neutral-950 via-purple-950 to-indigo-950">

      {/* HERO */}
      <div className="pt-32 pb-6 px-8 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-7xl font-black bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent"
        >
          Mission Calendar
        </motion.h1>
        <p className="text-white/60 text-xl mt-4">Holidays. Deadlines. Victory.</p>
      </div>

      {/* FILTERS */}
      <div className="max-w-7xl mx-auto px-8 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-6 flex flex-wrap gap-6 items-center justify-center"
        >
          {user.role === "hr" && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddModal(true)}
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl text-white font-bold shadow-xl hover:shadow-cyan-500/50 flex items-center gap-3"
            >
              <FaPlus /> Add Holiday
            </motion.button>
          )}

          <div className="flex gap-4">
            {[
              { key: "company", label: "Company", icon: "Office" },
              { key: "public", label: "Public", icon: "Globe" },
              { key: "tasks", label: "Tasks", icon: "Tasks" }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilters(prev => ({ ...prev, [f.key]: !prev[f.key] }))}
                className={`px-6 py-3 rounded-2xl font-bold transition-all ${
                  filters[f.key]
                    ? "bg-white/20 text-white shadow-lg"
                    : "bg-white/5 text-white/50"
                }`}
              >
                {f.icon} {f.label}
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* CALENDAR */}
      <div className="max-w-7xl mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl"
        >
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            eventPropGetter={eventStyle}
            onSelectEvent={setSelectedEvent}
            views={["month", "week", "day"]}
            className="text-white"
          />
        </motion.div>
      </div>

      {/* EVENT DETAIL MODAL */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-6">
                {selectedEvent.type === "company" && <FaStar className="text-emerald-300 text-3xl" />}
                {selectedEvent.type === "public" && <FaGlobe className="text-purple-300 text-3xl" />}
                {selectedEvent.type === "task" && <FaTasks className="text-orange-300 text-3xl" />}
                <h3 className="text-2xl font-bold text-white">{selectedEvent.title}</h3>
              </div>
              <p className="text-cyan-300 text-lg mb-4">
                {selectedEvent.start.toLocaleDateString("en-IN", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              {selectedEvent.desc && (
                <p className="text-white/80 mb-6 italic">"{selectedEvent.desc}"</p>
              )}
              <div className="flex justify-end">
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="px-8 py-4 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ADD HOLIDAY MODAL */}
      <AnimatePresence>
        {showAddModal && user.role === "hr" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Add Company Holiday</h2>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                await addDoc(collection(db, "holidays"), {
                  title: form.title.value,
                  date: form.date.value,
                  description: form.desc.value,
                  type: "company",
                  createdBy: user.email,
                  createdAt: serverTimestamp()
                });
                setShowAddModal(false);
              }}>
                <input
                  name="title"
                  placeholder="Holiday Name"
                  required
                  className="w-full mb-4 px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50"
                />
                <input
                  name="date"
                  type="date"
                  required
                  className="w-full mb-4 px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white"
                />
                <textarea
                  name="desc"
                  placeholder="Description (optional)"
                  rows={3}
                  className="w-full mb-6 px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50"
                />
                <div className="flex gap-4">
                  <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 py-4 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl text-white font-bold shadow-xl hover:shadow-emerald-500/50">
                    Add Holiday
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FOOTER */}
      <div className="text-center mt-20 text-white/40 text-sm">
        <p>Powered by <strong className="text-cyan-300">DriveEasy HR</strong> • Real-time • Cosmic</p>
      </div>
    </div>
  );
};

export default CalendarPage;